// El corte por cuenta de lo que el Chat guarda en `sessionStorage` —
// ADR-2026-09-09-el-chat-guardado-lleva-dueno (supersede el mecanismo de
// ADR-2026-08-17-chat-atlas-corte-por-cuenta en la parte del primer render).
//
// El motor RESTAURA sus mensajes y su `chat_id` de `sessionStorage` al montar. El corte
// que había era "en vivo": el provider se remonta con `key` = id del usuario y, antes,
// se limpian las claves. Funciona mientras el cambio de cuenta pase por un render.
//
// El logout NO pasa por un render garantizado: `lib/cerrarSesion` hace
// `window.location.replace`, una navegación DURA, y nada asegura que React llegue a
// pintar el `SIGNED_OUT` antes de que el documento se vaya. `sessionStorage` es de la
// PESTAÑA y sobrevive esa recarga. La cuenta siguiente entra entonces por el camino
// `null → id` —el primer resolve, que se saltea a propósito porque "no es un cambio de
// cuenta, es la cuenta llegando"— y el chat del que se fue reaparecía entero, con su
// `chat_id` incluido (o sea: los mensajes nuevos se escribían en la fila de `agent_chats`
// del OTRO usuario).
//
// La fila del dueño arregla eso sin depender de que ninguna limpieza haya alcanzado a
// correr: lo guardado dice de quién es, y se compara ANTES de restaurar.
//
// Puro (recibe el `Storage`) para poder probarlo sin DOM.

import { clavesDeStorage } from '../pages/v2/useV2Chat';
import { PREFIJO_STORAGE_CHAT } from './interruptoresChat';

/** Lo mínimo que estas funciones usan de `Storage`. */
export interface StorageMinimo {
  getItem: (clave: string) => string | null;
  setItem: (clave: string, valor: string) => void;
  removeItem: (clave: string) => void;
}

/** De quién es la conversación guardada. Al lado de las claves del motor. */
export const CLAVE_DUENO_DEL_CHAT = `${PREFIJO_STORAGE_CHAT}-owner`;

/** El `sessionStorage` del navegador, o null donde no exista o esté bloqueado. */
export function storageDeSesion(): StorageMinimo | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Borra las claves de sesión del motor del Chat (mensajes, chat actual, investigación)
 *  y el dueño. Se llama al cambiar de cuenta. */
export function limpiarSesionDelChat(storage: StorageMinimo | null = storageDeSesion()): void {
  if (!storage) return;
  const claves = clavesDeStorage(PREFIJO_STORAGE_CHAT);
  try {
    storage.removeItem(claves.mensajes);
    storage.removeItem(claves.chatId);
    storage.removeItem(claves.research);
    storage.removeItem(CLAVE_DUENO_DEL_CHAT);
  } catch {
    /* noop */
  }
}

/**
 * Deja la sesión guardada lista para `usuarioId`: si es de OTRA cuenta, se borra.
 * Devuelve true cuando hubo que borrar (para poder afirmarlo en un test).
 *
 * Con `usuarioId` null no toca nada: eso es "todavía no sé quién sos", no "no hay nadie"
 * — el logout lo limpia por su lado. Y sin dueño estampado tampoco borra: es lo guardado
 * por una versión anterior o por esta misma cuenta antes de que existiera la marca, y
 * tirarle la conversación a alguien que no cambió de cuenta sería el bug simétrico.
 */
export function asegurarDuenoDeLaSesion(
  usuarioId: string | null,
  storage: StorageMinimo | null = storageDeSesion(),
): boolean {
  if (!usuarioId || !storage) return false;
  try {
    const dueno = storage.getItem(CLAVE_DUENO_DEL_CHAT);
    const ajena = dueno !== null && dueno !== usuarioId;
    if (ajena) limpiarSesionDelChat(storage);
    storage.setItem(CLAVE_DUENO_DEL_CHAT, usuarioId);
    return ajena;
  } catch {
    /* storage bloqueado: no hay nada guardado que pueda cruzarse */
    return false;
  }
}
